// Branded shareable result card, rendered on a canvas ON DEVICE — zero network,
// zero storage. This is the acquisition loop: the image escapes the app
// (WhatsApp/Instagram) carrying the score, the "beat X of N" brag, and the
// presenter's public page URL (the funnel's return hook).
//
// Colors/fonts mirror the "midnight game show" tokens in tailwind.config.js —
// keep them in sync by hand (canvas can't read Tailwind classes).

const INK = '#16092E';
const INK_SOFT = '#241341';
const ACCENT = '#FFC93C';
const TILE_COLORS = ['#E21B3C', '#1368CE', '#D89E00', '#1F9E0C'];
const SHAPES = ['▲', '◆', '●', '■'];

const DISPLAY = '"Bricolage Grotesque", "Plus Jakarta Sans", system-ui, sans-serif';
const BODY = '"Plus Jakarta Sans", system-ui, sans-serif';

export interface ResultCardData {
  quizTitle: string;
  playerName: string;
  avatar?: string;
  score: number;
  rank?: number; // 1-based, only when the player made the published top-N
  totalPlayers?: number;
  presenterName?: string;
  presenterUrl?: string; // absolute /p/:uid URL printed on the card
}

const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// Trim with an ellipsis so a long quiz title can't overflow the card.
const fitText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
};

export const renderResultCard = async (data: ResultCardData): Promise<Blob> => {
  // Square reads best in chat apps; 1080 is plenty for retina without a huge file.
  const SIZE = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');

  // Canvas ignores CSS @font-face until the faces are actually loaded.
  try {
    await Promise.all([
      (document as any).fonts?.load(`800 96px ${DISPLAY}`),
      (document as any).fonts?.load(`600 40px ${BODY}`),
    ]);
  } catch { /* system fallback still renders a valid card */ }

  // Stage
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Brand row
  ctx.textAlign = 'center';
  ctx.fillStyle = ACCENT;
  ctx.font = `800 52px ${DISPLAY}`;
  ctx.fillText('QuizLive', SIZE / 2, 110);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = `600 30px ${BODY}`;
  ctx.fillText('LIVE QUIZ RESULT', SIZE / 2, 158);

  // The four-tile grammar — the one multi-color moment, same as the app.
  const tileW = 84;
  const gap = 24;
  const rowW = 4 * tileW + 3 * gap;
  let tx = (SIZE - rowW) / 2;
  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = TILE_COLORS[i];
    roundRect(ctx, tx, 200, tileW, tileW, 18);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = `48px ${BODY}`;
    ctx.fillText(SHAPES[i], tx + tileW / 2, 200 + tileW / 2 + 17);
    tx += tileW + gap;
  }

  // Player identity
  const identity = `${data.avatar ? `${data.avatar} ` : ''}${data.playerName}`;
  ctx.fillStyle = '#fff';
  ctx.font = `800 64px ${DISPLAY}`;
  ctx.fillText(fitText(ctx, identity, SIZE - 160), SIZE / 2, 420);

  // Score panel — accent means winner/meaning, nothing else on the card is gold.
  ctx.fillStyle = INK_SOFT;
  roundRect(ctx, 140, 470, SIZE - 280, 280, 32);
  ctx.fill();
  ctx.strokeStyle = ACCENT;
  ctx.lineWidth = 4;
  roundRect(ctx, 140, 470, SIZE - 280, 280, 32);
  ctx.stroke();

  ctx.fillStyle = ACCENT;
  ctx.font = `800 128px ${DISPLAY}`;
  ctx.fillText(`${data.score.toLocaleString()} pts`, SIZE / 2, 610);

  const framing =
    data.rank != null
      ? `Finished #${data.rank}${data.totalPlayers ? ` of ${data.totalPlayers} players` : ''}`
      : data.totalPlayers
        ? `One of ${data.totalPlayers} players`
        : 'Played along live';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.font = `700 44px ${BODY}`;
  ctx.fillText(framing, SIZE / 2, 690);

  // Quiz title
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = `600 40px ${BODY}`;
  ctx.fillText(fitText(ctx, `“${data.quizTitle}”`, SIZE - 200), SIZE / 2, 810);

  // Return hook — the presenter's public page.
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = `600 34px ${BODY}`;
  ctx.fillText(
    data.presenterName ? `Play the next one with ${data.presenterName}` : 'Join the next live quiz',
    SIZE / 2,
    930
  );
  if (data.presenterUrl) {
    ctx.fillStyle = ACCENT;
    ctx.font = `700 36px ${BODY}`;
    ctx.fillText(fitText(ctx, data.presenterUrl.replace(/^https?:\/\//, ''), SIZE - 160), SIZE / 2, 985);
  }

  // Maker's mark, small and out of the way.
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = `600 26px ${BODY}`;
  ctx.fillText('QuizLive · by aliensbrain', SIZE / 2, 1048);

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
  );
};

// Share the card as an IMAGE where the OS supports it (Web Share Level 2 —
// most phones); fall back to a plain download so desktop users still get the
// artifact. Returns what actually happened so the button can label itself.
export const shareResultCard = async (
  data: ResultCardData,
  shareText: string
): Promise<'shared' | 'downloaded'> => {
  const blob = await renderResultCard(data);
  const file = new File([blob], 'quizlive-result.png', { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    await nav.share({ files: [file], text: shareText });
    return 'shared';
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'quizlive-result.png';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
};
